import { cn } from "@/core/utils";
import { MessageBubbleTypes } from "../types";
import { ChartExpand } from "./chart-expand";
import { BlocksRenderer } from "./blocks-renderer";
import { AddedListCard } from "./added-list-card";
import { formatMarkdown } from "../utils/formatMarkdown";
import { MarkdownMessage } from "./markdown-message";
import { SuggestionCards } from "./suggestion-cards";
import { FeedbackButtons } from "./feedback-buttons";
import FileUploadIcon from "@/_old/assets/icons/file-upload.svg?react";
import { useState, useEffect } from "react";
import { AssistantIdentity } from "./assistant-identity";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function MessageBubble({
  message,
  userInitials,
  onSuggestionClick,
}: MessageBubbleTypes) {
  const isUser = message.isUser;
  const [feedback, setFeedback] = useState<"like" | "dislike" | null>(
    message.feedback || null,
  );

  // Carregar feedback do localStorage ao montar
  useEffect(() => {
    if (!isUser && message.id) {
      try {
        const key = `feedback:${message.id}`;
        const savedFeedback = localStorage.getItem(key);
        if (savedFeedback === "like" || savedFeedback === "dislike") {
          setFeedback(savedFeedback);
        }
      } catch (error) {
        console.error("Error loading feedback from localStorage:", error);
      }
    }
  }, [message.id, isUser]);

  // Blocks "largos" (gráfico/tabela) devem ocupar 100% da largura da mensagem — do contrário
  // a coluna flex encolhe pro conteúdo e espreme o ResponsiveContainer. Só esticamos a coluna
  // quando há um bloco largo; bolhas de texto e chips continuam com a largura de conteúdo.
  const hasWideBlock =
    Boolean(message.chart) ||
    (message.blocks?.some(
      (b) =>
        b.type === "chart" ||
        b.type === "table" ||
        b.type === "card_list" ||
        b.type === "kpi_group" ||
        b.type === "schedule_form" ||
        b.type === "schedule_list",
    ) ??
      false);

  return (
    <div
      className={cn(
        "flex gap-2 max-w-[98%]",
        isUser ? "ml-auto flex-row-reverse" : "mr-auto",
      )}
    >
      <div
        className={cn(
          // min-w-0 permite que o balão encolha e quebre a linha em vez de estourar
          // à direita (overflow) no chat reduzido.
          "flex min-w-0 flex-col",
          isUser ? "items-end" : "items-start",
          // estica a coluna do bot só quando há bloco largo (gráfico/tabela)
          !isUser && hasWideBlock && "w-full",
        )}
      >
        {!isUser && <AssistantIdentity agentType={message.agent_type} />}
        {isUser && message.attachment ? (
          <div className="flex min-w-0 max-w-[380px] items-center justify-center gap-3 rounded-[16px] bg-aiBlue-100 p-4">
            <FileUploadIcon className="w-10 h-8 shrink-0" aria-hidden />
            <div className="flex-1 min-w-0">
              <p
                className="truncate text-sm font-bold text-[#333]"
                title={message.attachment.fileName}
              >
                {message.attachment.fileName}
              </p>
              <p className="text-sm font-normal text-[#5C5C5C]">
                {`${formatFileSize(message.attachment.fileSize)} de ${formatFileSize(message.attachment.fileSize)}`}
              </p>
            </div>
          </div>
        ) : message.chart ? (
          <div className="w-full rounded-lg p-0 text-sm">
            <ChartExpand spec={message.chart} />
            {message.content && (
              <div className="px-3 py-2 mt-2 text-sm rounded-lg bg-lightgray-200 text-neutral-800">
                {formatMarkdown(message.content)}
              </div>
            )}
          </div>
        ) : (
          <div
            className={cn(
              "break-words rounded-lg text-md",
              isUser
                ? "whitespace-pre-wrap bg-lightgray-100 px-4 md:px-[1.7rem] py-2 text-darkgray-600"
                : message.isTyping
                  ? "agent-status-loading py-0 px-0 md:px-[1.7rem] font-body text-base font-medium leading-6"
                  : "px-0 md:px-[1.7rem] py-2 text-darkgray-600",
            )}
            role={message.isTyping ? "status" : undefined}
            aria-live={message.isTyping ? "polite" : undefined}
            aria-atomic={message.isTyping ? "true" : undefined}
          >
            {isUser ? (
              message.content
            ) : (
              <>
                {(() => {
                  const hasAddedList =
                    message.response_added_list &&
                    message.response_added_list.emails?.length > 0;
                  const hasExpandableList =
                    message.response_expandable &&
                    message.response_expandable.emails?.length > 0;
                  const hasListCard = hasAddedList || hasExpandableList;
                  const listLabel = hasAddedList
                    ? message.response_added_list!.label
                    : hasExpandableList
                      ? message.response_expandable!.label
                      : "";
                  const listEmails = hasAddedList
                    ? message.response_added_list!.emails
                    : hasExpandableList
                      ? message.response_expandable!.emails
                      : [];
                  if (hasListCard && listEmails.length > 0 && message.content) {
                    const firstBreak = message.content.indexOf("\n\n");
                    const intro =
                      firstBreak >= 0
                        ? message.content.slice(0, firstBreak)
                        : message.content;
                    const rest =
                      firstBreak >= 0
                        ? message.content.slice(firstBreak).trimStart()
                        : "";
                    return (
                      <>
                        {formatMarkdown(intro)}
                        <AddedListCard label={listLabel} emails={listEmails} />
                        {rest ? formatMarkdown(rest) : null}
                      </>
                    );
                  }
                  return <MarkdownMessage content={message.content} />;
                })()}
              </>
            )}
          </div>
        )}

        {/* RFC T1+: render structured blocks (chart, chips, expandable_list, ...) abaixo do
            texto. O texto acima segue como fallback; blocks são aditivos. `onSuggestionClick`
            é repassado para os `chips` reaproveitarem o mesmo fluxo das sugestões legadas. */}
        {!isUser && message.blocks && message.blocks.length > 0 && (
          <div className={cn("mt-3", hasWideBlock ? "w-full" : "")}>
            <BlocksRenderer
              blocks={message.blocks}
              onSuggestionClick={onSuggestionClick}
            />
          </div>
        )}

        {/* Render time_saved message for bot messages */}
        {!isUser && message.time_saved && (
          <div className="px-4 py-3 mx-4 mt-3 text-base font-medium break-words rounded-2xl border border-aiBlue-300 bg-ai-time-saved">
            <span
              style={{
                backgroundImage:
                  "linear-gradient(42deg, #020C61 10.32%, #0A178C 36.43%, #3246F5 62.18%, #3246F5 85.75%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              {message.time_saved.message}
            </span>
          </div>
        )}

        {/* Render suggestions only for bot messages */}
        {!isUser &&
          message.suggestions &&
          message.suggestions.length > 0 &&
          onSuggestionClick && (
            <SuggestionCards
              suggestions={message.suggestions}
              onSuggestionClick={onSuggestionClick}
              isGreeting={message.id === "greeting-1"}
            />
          )}

        {/* Render feedback buttons only for bot messages */}
        {!isUser && !message.isTyping && (
          <FeedbackButtons
            runId={message.run_id}
            currentFeedback={feedback}
            onFeedbackChange={setFeedback}
            messageId={message.id}
            messageContent={message.content}
          />
        )}
      </div>
    </div>
  );
}
